import { useMemo } from "react";
import {
  Target as TargetTree,
  TargetSkill as TargetSkillTree,
} from "../../utils/CourseTree";
import { CourseLanguageOverrides, TargetGroup } from "@backend-types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  Stack,
  Card,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { ModuleTargetList } from "./ModuleTargetList";
import { GroupMemberLockMap, LockStatus, TargetGroupMap } from "../../../interfaces";
import useStorageState from "../../../hooks/useStorageState";

const useStyles = makeStyles((theme) => ({
  accordionRoot: {
    padding: "0px",
    backgroundColor: "transparent",
    overflow: "hidden",
    boxSizing: "border-box",
    margin: "0px !important",
    "& .MuiAccordionSummary-root": {
      minHeight: "unset !important",
    },
  },
  accordionSummary: {
    margin: "0px !important",
    padding: "24px",
    borderRadius: "8px",
    "& .MuiAccordionSummary-content": {
      margin: "0px !important",
    },

    "& .MuiAccordionSummary-expandIconWrapper": {
      marginTop: "0.5em",
      marginBottom: "auto",
    },
  },
  accordionDetails: {
    marginBottom: "24px",
  },
  accordionHeading: {
    fontWeight: 700,
    lineHeight: "34px",
    color: theme.palette.text.primary,
    [theme.breakpoints.down("md")]: {
      fontSize: "16px",
      lineHeight: "26px",
    },
  },
  accordionGroupSummary: {
    color: "#474747",
    fontWeight: 400,
    lineHeight: "34px",
    textWrap: "pretty",
    overflowWrap: "anywhere",
    [theme.breakpoints.down("md")]: {
      fontSize: "16px",
      lineHeight: "26px",
    },
  },
  expandChevron: {
    fontSize: "24px",
    color: theme.palette.text.primary,
    [theme.breakpoints.down("md")]: {
      fontSize: "16px",
      lineHeight: "26px",
    },
  },
}));

interface ModuleTargetGroupListProps {
  targetGroups: TargetGroup[];
  targets: TargetTree[];
  openSkillVideoModal: (skills: TargetSkillTree[] | undefined) => void;
  languageOverrides?: CourseLanguageOverrides;
}

export const ModuleTargetGroupList = ({
  targetGroups,
  targets,
  openSkillVideoModal,
  languageOverrides,
}: ModuleTargetGroupListProps) => {
  const classes = useStyles();

  const [targetLastSeen] = useStorageState({
    storageKey: "lxp-lastTarget",
  });

  const groupTargetMap = useMemo<TargetGroupMap>(() => {
    const newMap = new Map<string, TargetTree[]>();
    targetGroups.forEach((targetGroup) => {
      const groupMemberIds = targetGroup.target_group_members.map(
        (item) => item.target_id
      );
      const filteredTargets = targets.filter((item) => groupMemberIds.includes(item.id));
      newMap.set(targetGroup.id, filteredTargets);
    });
    return newMap;
  }, [targets, targetGroups]);

  const groupMemberLockMap = useMemo<GroupMemberLockMap>(() => {
    const newMap = new Map<string, Map<string, LockStatus>>();
    targetGroups.forEach((targetGroup) => {
      const lockMap = new Map<string, LockStatus>();
      targetGroup.target_group_members.forEach((targetGroupMember) => {
        const lockUntil = targetGroupMember.metadata?.lock_until;
        const completion = targets
          .filter((target) => lockUntil?.targets_completed?.includes(target.id))
          .map((target) => target.isCompleted());
        const allComplete = completion.every((v) => v === true);

        const lockStatus: LockStatus = {
          is_locked: lockUntil !== undefined && lockUntil !== null && !allComplete,
          tooltip: targetGroupMember.metadata?.lock_until?.tooltip ?? "Locked",
          label: targetGroupMember.metadata?.lock_until?.tooltip ?? "Locked",
        };
        lockMap.set(targetGroupMember.target_id, lockStatus);
      });

      newMap.set(targetGroup.id, lockMap);
    });

    return newMap;
  }, [targetGroups, targets]);

  return (
    <>
      {targetGroups.map((targetGroup, index) => (
        <Card
          sx={{
            mb: index === targetGroups.length - 1 ? 0 : "24px", // Remove margin for the last item
            "&:hover": {
              boxShadow: "0px 8px 12px 0px rgba(0, 0, 0, 0.16)",
            },
          }}>
          <Accordion
            defaultExpanded={
              targetLastSeen !== undefined &&
              targetLastSeen !== null &&
              targetGroup.target_group_members
                .map((item) => item.target_id)
                .includes(targetLastSeen)
            }
            key={targetGroup.id}
            className={classes.accordionRoot}>
            <AccordionSummary
              className={classes.accordionSummary}
              expandIcon={
                <i className={`${classes.expandChevron} fa-solid fa-chevron-down`}></i>
              }>
              <Stack direction="column" spacing={2}>
                <Typography
                  variant="h4"
                  component="h5"
                  className={classes.accordionHeading}>
                  {targetGroup.name}
                </Typography>
                <Typography
                  variant="body2"
                  lineHeight="24px"
                  className={classes.accordionGroupSummary}>
                  {targetGroup.summary}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails
              className={classes.accordionDetails}
              sx={{ padding: "0px !important" }}>
              <ModuleTargetList
                targets={groupTargetMap.get(targetGroup.id) ?? []}
                targetLockMap={groupMemberLockMap.get(targetGroup.id)}
                openSkillVideoModal={openSkillVideoModal}
                languageOverrides={languageOverrides}
                groupTargets={true}
              />
            </AccordionDetails>
          </Accordion>
        </Card>
      ))}
    </>
  );
};
